<template>
  <div class="space-y-6">
    <!-- Toolbar -->
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="flex flex-wrap items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @click="showCreateModal = true"
        >
          <vue-feather type="plus" size="16" class="mr-2" />
          New Version Entry
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          @click="fetchDeviceVersions"
        >
          <vue-feather
            :type="loadingDeviceVersions ? 'loader' : 'refresh-cw'"
            size="16"
            class="mr-2"
            :class="{ 'animate-spin': loadingDeviceVersions }"
          />
          Refresh
        </button>
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <select
          v-model="selectedProduct"
          class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
          @change="onProductChange"
        >
          <option value="">All Products</option>
          <option
            v-for="option in productOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <select
          v-model="selectedFlavor"
          class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
          @change="onFlavorChange"
        >
          <option value="">All Flavors</option>
          <option
            v-for="option in flavorOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <select
          v-model="selectedPlatform"
          class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
          @change="onPlatformChange"
        >
          <option value="">All Platforms</option>
          <option v-for="p in platforms" :key="p" :value="p">{{ p }}</option>
        </select>
      </div>
    </div>

    <!-- Table -->
    <div
      class="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
      <div class="overflow-x-auto">
        <table class="w-full table-fixed text-left text-sm">
          <thead
            class="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
          >
            <tr>
              <th
                class="w-[12%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
              >
                Platform
              </th>
              <th
                class="w-[17%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
              >
                Target
              </th>
              <th
                class="w-[13%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
              >
                Min Required
              </th>
              <th
                class="w-[14%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
              >
                Current Version
              </th>
              <th
                class="w-[23%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
              >
                Note
              </th>
              <th
                class="w-[12%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
              >
                Updated
              </th>
              <th
                class="w-[9%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingDeviceVersions">
              <td colspan="7" class="px-4 py-8 text-center text-gray-500">
                <vue-feather
                  type="loader"
                  size="20"
                  class="animate-spin inline-block"
                />
                <span class="ml-2">Loading...</span>
              </td>
            </tr>
            <tr v-else-if="deviceVersions.length === 0">
              <td colspan="7" class="px-4 py-8 text-center text-gray-500">
                No device versions found.
              </td>
            </tr>
            <tr
              v-for="dv in deviceVersions"
              :key="dv.id"
              class="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
            >
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  :class="platformBadgeClass(dv.client_device_platform)"
                >
                  {{ dv.client_device_platform }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs text-gray-700 dark:text-gray-300">
                <span class="font-medium">{{ targetLabel(dv) }}</span>
              </td>
              <td
                class="px-4 py-3 font-mono font-medium text-gray-900 dark:text-white"
              >
                {{ dv.minimum_required_version }}
              </td>
              <td class="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">
                {{ dv.current_version }}
              </td>
              <td
                class="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs truncate max-w-[250px]"
              >
                {{ dv.note || "—" }}
              </td>
              <td class="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                {{ formatDate(dv.updated_at || dv.created_at) }}
              </td>
              <td class="px-4 py-3">
                <button
                  @click="editVersion(dv)"
                  class="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400"
                  title="Edit"
                >
                  <vue-feather type="edit-2" size="14" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700"
      >
        <span class="text-sm text-gray-500 dark:text-gray-400">
          {{ totalDeviceVersions }} entr{{
            totalDeviceVersions !== 1 ? "ies" : "y"
          }}
          total
        </span>
        <div class="flex gap-1">
          <button
            v-for="page in totalPages"
            :key="page"
            @click="goToPage(page)"
            class="rounded px-3 py-1 text-sm"
            :class="
              page === currentPage
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
            "
          >
            {{ page }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <modal-view
      v-if="showCreateModal"
      title="New Device Version"
      @close-modal="showCreateModal = false"
    >
      <form @submit.prevent="handleCreate" class="space-y-5 p-4">
        <div class="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Product *</label
            >
            <select
              v-model="createForm.product_code"
              required
              class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            >
              <option
                v-for="option in productOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Flavor *</label
            >
            <select
              v-model="createForm.flavor_code"
              required
              class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            >
              <option
                v-for="option in flavorOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Platform *</label
          >
          <select
            v-model="createForm.client_device_platform"
            required
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
          >
            <option value="" disabled>Select platform</option>
            <option v-for="p in platforms" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Minimum Required Version *</label
          >
          <input
            v-model="createForm.minimum_required_version"
            type="text"
            placeholder="e.g. 2.0.0"
            required
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <p class="mt-1 text-xs text-gray-500">
            Clients below this version will be forced to update
          </p>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Current Version *</label
          >
          <input
            v-model="createForm.current_version"
            type="text"
            placeholder="e.g. 2.1.0"
            required
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Note</label
          >
          <textarea
            v-model="createForm.note"
            rows="3"
            placeholder="Optional note..."
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          ></textarea>
        </div>
        <p v-if="createError" class="text-sm text-red-600 dark:text-red-400">
          {{ createError }}
        </p>
        <div class="flex justify-end gap-3 pt-2">
          <button
            type="button"
            @click="showCreateModal = false"
            class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="createSaving"
            class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {{ createSaving ? "Creating..." : "Create" }}
          </button>
        </div>
      </form>
    </modal-view>

    <!-- Edit Modal -->
    <modal-view
      v-if="showEditModal"
      title="Edit Device Version"
      @close-modal="showEditModal = false"
    >
      <div class="space-y-5 p-4">
        <div class="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
          <div>
            <span class="text-xs text-gray-500 dark:text-gray-400">Target</span>
            <p class="font-medium text-gray-900 dark:text-white">
              {{ targetLabel(editForm) }}
            </p>
          </div>
          <div>
            <span class="text-xs text-gray-500 dark:text-gray-400">Platform</span>
            <p class="font-medium text-gray-900 dark:text-white">
              {{ editForm.client_device_platform }}
            </p>
          </div>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Minimum Required Version</label
          >
          <input
            v-model="editForm.minimum_required_version"
            type="text"
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Current Version</label
          >
          <input
            v-model="editForm.current_version"
            type="text"
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Note</label
          >
          <textarea
            v-model="editForm.note"
            rows="3"
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          ></textarea>
        </div>
        <p v-if="editError" class="text-sm text-red-600 dark:text-red-400">
          {{ editError }}
        </p>
        <div class="flex justify-end gap-3 pt-2">
          <button
            type="button"
            @click="showEditModal = false"
            class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            @click="handleUpdate"
            :disabled="editSaving"
            class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {{ editSaving ? "Saving..." : "Save Changes" }}
          </button>
        </div>
      </div>
    </modal-view>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ModalView from "@/components/modals/ModalView.vue";
import {
  RELEASE_FLAVORS,
  RELEASE_PRODUCTS,
  releaseTargetLabel,
} from "@/constants/releaseTargets";

export default {
  name: "DeviceVersionsTab",
  components: { ModalView },
  data() {
    return {
      selectedProduct: "",
      selectedFlavor: "",
      selectedPlatform: "",
      showCreateModal: false,
      showEditModal: false,
      createSaving: false,
      editSaving: false,
      createError: "",
      editError: "",
      createForm: {
        product_code: "HOVASTORE",
        flavor_code: "BASE",
        client_device_platform: "",
        minimum_required_version: "",
        current_version: "",
        note: "",
      },
      editForm: {
        id: null,
        product_code: "HOVASTORE",
        flavor_code: "BASE",
        client_device_platform: "",
        minimum_required_version: "",
        current_version: "",
        note: "",
      },
      platforms: ["ANDROID", "IOS", "WINDOWS", "MAC", "LINUX", "WEB"],
      productOptions: RELEASE_PRODUCTS,
      flavorOptions: RELEASE_FLAVORS,
    };
  },
  computed: {
    ...mapGetters("adminDeviceVersions", [
      "deviceVersions",
      "totalDeviceVersions",
      "loadingDeviceVersions",
      "currentPage",
      "totalPages",
    ]),
  },
  methods: {
    ...mapActions("adminDeviceVersions", [
      "fetchDeviceVersions",
      "createDeviceVersion",
      "updateDeviceVersion",
      "setPage",
      "setPlatformFilter",
      "setProductFilter",
      "setFlavorFilter",
    ]),
    onPlatformChange() {
      this.setPlatformFilter(this.selectedPlatform);
    },
    onProductChange() {
      this.setProductFilter(this.selectedProduct);
    },
    onFlavorChange() {
      this.setFlavorFilter(this.selectedFlavor);
    },
    goToPage(page) {
      this.setPage(page);
    },
    editVersion(dv) {
      this.editForm = {
        id: dv.id,
        product_code: dv.product_code,
        flavor_code: dv.flavor_code,
        client_device_platform: dv.client_device_platform,
        minimum_required_version: dv.minimum_required_version,
        current_version: dv.current_version,
        note: dv.note || "",
      };
      this.editError = "";
      this.showEditModal = true;
    },
    async handleCreate() {
      this.createSaving = true;
      this.createError = "";
      try {
        await this.createDeviceVersion({
          product_code: this.createForm.product_code,
          flavor_code: this.createForm.flavor_code,
          client_device_platform: this.createForm.client_device_platform,
          minimum_required_version: this.createForm.minimum_required_version,
          current_version: this.createForm.current_version,
          note: this.createForm.note || null,
        });
        this.showCreateModal = false;
        this.createForm = {
          product_code: "HOVASTORE",
          flavor_code: "BASE",
          client_device_platform: "",
          minimum_required_version: "",
          current_version: "",
          note: "",
        };
      } catch (error) {
        this.createError =
          error?.response?.data?.message || "Failed to create device version.";
      } finally {
        this.createSaving = false;
      }
    },
    async handleUpdate() {
      this.editSaving = true;
      this.editError = "";
      try {
        await this.updateDeviceVersion({
          deviceVersionId: this.editForm.id,
          payload: {
            minimum_required_version: this.editForm.minimum_required_version,
            current_version: this.editForm.current_version,
            note: this.editForm.note,
          },
        });
        this.showEditModal = false;
      } catch (error) {
        this.editError =
          error?.response?.data?.message || "Failed to update device version.";
      } finally {
        this.editSaving = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    targetLabel(record) {
      return releaseTargetLabel(record);
    },
    platformBadgeClass(platform) {
      const map = {
        WINDOWS:
          "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        MAC: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
        LINUX:
          "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
        ANDROID:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        IOS: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
        WEB: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200",
      };
      return map[platform] || "bg-gray-100 text-gray-800";
    },
  },
  mounted() {
    this.fetchDeviceVersions();
  },
};
</script>
